import { createFileRoute } from "@tanstack/react-router";
import { PolicyLayout } from "@/components/PolicyLayout";

export const Route = createFileRoute("/policies/fair-use-policy")({
  component: FairUsePolicy,
  head: () => ({
    meta: [
      { title: "Fair Use Policy. TruWe Free Diet Plan" },
      {
        name: "description",
        content:
          "How TruWe's free diet plan and routine generator may be used, what it is not, and how the information you share is handled.",
      },
      { property: "og:title", content: "Fair Use Policy. TruWe Free Diet Plan" },
      {
        property: "og:description",
        content:
          "Fair use rules for TruWe's free diet plan generator: educational guidance only, not medical advice.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://truwe.in/policies/fair-use-policy" }],
  }),
});

function FairUsePolicy() {
  return (
    <PolicyLayout title="Fair Use Policy" updated="September 2026">
      <p>
        This Fair Use Policy applies to TruWe's free tools — in particular the free diet plan and
        daily routine generator — and to the information you share with us while using them. Read it
        together with our <a href="/policies/privacy-policy">Privacy Policy</a> and{" "}
        <a href="/policies/terms-of-service">Terms of Service</a>.
      </p>

      <h2>1. What the generator is</h2>
      <p>
        The plan you receive is general nutrition and lifestyle education, built from the details you
        enter. It is not a diagnosis, not a prescription, and not a substitute for advice from your
        doctor, dietitian or another qualified professional. It does not name medicines or set doses.
      </p>

      <h2>2. When not to rely on it</h2>
      <ul>
        <li>If you are pregnant or breastfeeding, or under 18.</li>
        <li>If you are managing a condition under a clinician's care, or taking prescribed medicines.</li>
        <li>If you have a history of disordered eating, kidney or liver disease, or type 1 diabetes.</li>
      </ul>
      <p>
        In these situations, please speak with your clinician before changing what you eat. Some
        answers will stop the generator and show this message instead of a plan.
      </p>

      <h2>3. Fair use</h2>
      <ul>
        <li>The tool is for personal, non-commercial use. Enter your own details, honestly.</li>
        <li>
          Requests are rate-limited. Please do not script, scrape, bulk-generate or resell plans, or
          republish them as your own.
        </li>
        <li>Do not enter someone else's personal or health details without their permission.</li>
      </ul>

      <h2>4. The information you share</h2>
      <p>
        We use your name, email, mobile and the answers you give to build your plan, save it so you
        can return to it, and contact you about that plan. Health-related answers are treated as
        sensitive and are only used for this purpose.
      </p>
      <p>
        Promotional email and WhatsApp messages are optional and never required to get your plan. You
        only receive them if you tick the box, and you can withdraw at any time from{" "}
        <a href="/account/notifications">your notification settings</a>, by replying STOP on WhatsApp,
        or by using the unsubscribe link in any email.
      </p>

      <h2>5. Product mentions</h2>
      <p>
        Where a plan mentions a TruWe product, it is presented as optional nutritional support
        alongside food and routine — never as a treatment, cure, or requirement for the plan to work.
      </p>

      <h2>6. Changes and contact</h2>
      <p>
        We may update this policy as the tool evolves. Questions or a privacy request? Write to{" "}
        <a href="mailto:privacy@truwe.in">privacy@truwe.in</a>.
      </p>
    </PolicyLayout>
  );
}
